import { createSelector } from 'reselect'
import { RootState } from '../types'
import { primaryDeviceIccidSelector, watchEidSelector } from '../watch/watchSelectors'

export const dataStateSelector = (state: RootState) => state.data

export const isDataFetchPendingSelector = createSelector(dataStateSelector, data => data.isDataFetchPending)

export const dataFetchErrorSelector = createSelector(dataStateSelector, data => data.dataFetchError)

export const postOrdersMultisimProductIdRequestSelector = createSelector(
  dataStateSelector,
  data => data && data.isPostOrderRequest
)

export const patchOrdersMultisimDataRequestSelector = createSelector(
  dataStateSelector,
  data => data && data.isPatchOrderRequest
)

/* ------------- Users ------------- */
export const usersDataSelector = createSelector(dataStateSelector, data => data.usersData)

export const isMemberSelector = createSelector(
  usersDataSelector,
  usersData => !!usersData && !!usersData.attributes && !!usersData.attributes.familyRole
)

export const primaryPaymentMethodSelector = createSelector(usersDataSelector, (payment: any) => {
  const paymentMethod = payment && payment.paymentMethod
  if (paymentMethod && paymentMethod.type === 'CREDIT_CARD' && paymentMethod.details) {
    return paymentMethod.details.reduce((object, detail) => {
      if (detail.status === 'PRIMARY') {
        return detail
      }
      return object
    }, null)
  }
  return null
})

/* ------------- Products ------------- */
export const productsDataSelector = createSelector(dataStateSelector, data => data.productsData || [])

export const productMobileDataSelector = createSelector(
  productsDataSelector,
  products => products && products.find(product => product.specificationId === 'MOBILE_ACCESS')
)

export const simCardDataSelector = createSelector(
  productsDataSelector,
  products => products && products.find(product => product.specificationId === 'SIM_CARD')
)

export const isTheSameIccidDeviceSelector = createSelector(
  [simCardDataSelector, primaryDeviceIccidSelector],
  (simCardProduct, primaryDeviceIccid) => !!simCardProduct && simCardProduct.characteristic.sim === primaryDeviceIccid
)

export const isCorrectProductExistsSelector = createSelector(
  [productMobileDataSelector],
  productMobileData => !!productMobileData
)

export const isUserActiveSelector = createSelector(
  productMobileDataSelector,
  productData => !!productData && productData.status === 'Active'
)

export const productMultiSimsSelector = createSelector([
  productsDataSelector
], (products) => products.filter(product => product && product.specificationId === 'MULTI_SIM_CARD'))

export const productMultiSimEsimSelector = createSelector(
  [productMultiSimsSelector, watchEidSelector],
  (products, watchEid) =>
    products.find(
      product =>
        product.characteristic &&
        product.characteristic.eId === watchEid &&
        product.characteristic.eSimDevice === 'A_WATCH'
    )
)

export const exchangeMultisimSelector = createSelector([productMultiSimsSelector, productMultiSimEsimSelector], (multisimProducts, productMultiEsim) => {
  if (productMultiEsim) {
    return productMultiEsim
  }
  if (Array.isArray(multisimProducts) && multisimProducts.length > 0) {
    return multisimProducts[0]
  }
  return
})

/* ------------- Orders ------------- */
export const ordersDataSelector = createSelector(dataStateSelector, data => data.ordersData)

// export const ordersSelector = createSelector(ordersDataSelector, ordersData => ordersData && ordersData.orders)

// export const ongoingOrderSelector = createSelector(
//   ordersSelector,
//   orders =>
//     orders &&
//     orders.find(
//       order =>
//         order.status === 'Draft' &&
//         order.orderItem &&
//         !!order.orderItem.find(orderItemObject => orderItemObject.esimDevice === 'A_WATCH')
//     )
// )

export const postDataSelector = createSelector(ordersDataSelector, ordersData => ordersData && ordersData.postOrderData)

export const postOrdersMultisimProductIdSelector = createSelector(
  postDataSelector,
  postOrderData => postOrderData && postOrderData.id
)

export const patchOrderDataSelector = createSelector(
  ordersDataSelector,
  ordersData => ordersData && ordersData.patchOrderData
)

/* ------------- Esim ------------- */
export const esimDataSelector = createSelector(dataStateSelector, data => data.esimData || [])

// export const currentEsimDataSelector = createSelector(
//   [esimDataSelector, primaryDeviceIccidSelector],
//   (esimsData, primaryDeviceIccid) => esimsData.find(esimData => esimData.iccid === primaryDeviceIccid)
// )

export const esimDownloadedSelector = createSelector([esimDataSelector, watchEidSelector], (esimsData, watchEid) =>
  esimsData.find(esimData => esimData.subState === 'Downloaded' && watchEid === esimData.eId)
)

export const isEsimInProgressSelector = createSelector(
  [productMultiSimEsimSelector, esimDownloadedSelector],
  (product, esim) => !product && !!esim
)

export const esimOrderFinishedSelector = createSelector(
  [watchEidSelector, esimDataSelector, patchOrderDataSelector],
  (watchEid, esims, patchOrderData) => {
    if (patchOrderData) {
      return {
        iccid: patchOrderData.orderItem && patchOrderData.orderItem[0] && patchOrderData.orderItem[0].iccid,
        eId: patchOrderData.orderItem && patchOrderData.orderItem[0] && patchOrderData.orderItem[0].eid,
      }
    }
    return
    // else {
    //   const esim = esims && esims.find(esim => watchEid === esim.eId && ['Released'].includes(esim.subState))
    //   return (
    //     esim && {
    //       iccid: esim.iccid,
    //       eId: esim.eId,
    //     }
    //   )
    // }
  }
)

export const isEsimOrderFinishedSelector = createSelector(esimOrderFinishedSelector, esimOrder => !!esimOrder)

export const isEsimProductDownloadedSelector = createSelector(
  [esimDownloadedSelector, productMultiSimEsimSelector],
  (esimDownloaded, product) =>
    esimDownloaded && product && product.characteristic && product.characteristic.eId === esimDownloaded.eId
)

export const isAppleWatchEsimInstalledSelector = createSelector(
  [productMultiSimEsimSelector, esimDownloadedSelector],
  (product, esim) => !!(product && esim)
)
